import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { apiFetch } from '../../utils/apiClient';
import {
  buildDiscordConnectUrl,
  DISCORD_AUTH_ERROR_MESSAGES,
} from '../../utils/authUrls';

interface DiscordIdentityPanelProps {
  returnTo: string;
}

const DiscordIdentityPanel: React.FC<DiscordIdentityPanelProps> = ({ returnTo }) => {
  const { user, refreshUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authError = searchParams.get('auth_error');
  const connectedSuccess = searchParams.get('discord_connected') === '1';

  const authErrorMessage = useMemo(() => {
    if (!authError) return null;
    return DISCORD_AUTH_ERROR_MESSAGES[authError] || 'Discord connection failed. Please try again.';
  }, [authError]);

  React.useEffect(() => {
    if (!connectedSuccess && !authError) return;
    if (connectedSuccess) {
      void refreshUser();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('discord_connected');
    next.delete('auth_error');
    setSearchParams(next, { replace: true });
  }, [authError, connectedSuccess, refreshUser, searchParams, setSearchParams]);

  if (!user) return null;

  const handleConnect = () => {
    window.location.href = buildDiscordConnectUrl(returnTo);
  };

  const handleDisconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/auth/discord/disconnect', { method: 'POST' });
      if (!response.ok) {
        setError('Could not disconnect Discord. Please try again.');
        return;
      }
      await refreshUser();
    } catch {
      setError('Could not disconnect Discord. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const discordLabel =
    user.discord_display_name || user.discord_username || user.discord_id || 'Discord account';

  return (
    <div className="card space-y-4" data-testid="discord-identity-panel">
      <div>
        <h2 className="font-semibold text-white">Discord identity</h2>
        <p className="text-sm text-secondary-400 mt-1">
          Connect Discord to help claim your league profile from NeonSportz. Discord and Twitch
          identities are stored separately.
        </p>
      </div>

      {connectedSuccess && (
        <p className="text-sm text-green-300" data-testid="discord-connected-success">
          Discord connected successfully.
        </p>
      )}
      {(authErrorMessage || error) && (
        <p className="text-sm text-red-300" data-testid="discord-connect-error">
          {error || authErrorMessage}
        </p>
      )}

      {user.discord_connected ? (
        <div className="flex items-center gap-3" data-testid="discord-connected-profile">
          {user.discord_avatar_url ? (
            <img
              src={user.discord_avatar_url}
              alt=""
              className="h-10 w-10 rounded-full"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-secondary-700" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium truncate">{discordLabel}</p>
            {user.discord_username && (
              <p className="text-sm text-secondary-400 truncate">@{user.discord_username}</p>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDisconnect}
            disabled={loading}
            data-testid="disconnect-discord-button"
          >
            {loading ? 'Disconnecting...' : 'Disconnect Discord'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn-primary"
          onClick={handleConnect}
          data-testid="connect-discord-button"
        >
          Connect Discord
        </button>
      )}
    </div>
  );
};

export default DiscordIdentityPanel;
